import { Routes, Route, Navigate } from 'react-router-dom';

import { LoginPage } from './pages/LoginPage.jsx';
import { MfaChallengePage } from './pages/MfaChallengePage.jsx';
import { NotFoundPage } from './pages/NotFoundPage.jsx';
import { RequireAuth } from './auth/RequireAuth.jsx';
import { RoleRedirect } from './auth/RoleRedirect.jsx';

import { AdminLayout } from './layouts/AdminLayout.jsx';
import { EmployeeLayout } from './layouts/EmployeeLayout.jsx';
import { PetLayout } from './layouts/PetLayout.jsx';
import { KioskLayout } from './layouts/KioskLayout.jsx';

import { AdminDashboardPage } from './pages/admin/DashboardPage.jsx';
import { OperationsCenterPage } from './pages/admin/OperationsCenterPage.jsx';
import { UsersPage } from './pages/admin/users/UsersPage.jsx';
import { RolesPage } from './pages/admin/roles/RolesPage.jsx';
import { StoresPage } from './pages/admin/stores/StoresPage.jsx';
import { ProductsPage } from './pages/admin/products/ProductsPage.jsx';
import { ProductEditPage } from './pages/admin/products/ProductEditPage.jsx';
import { CategoriesPage } from './pages/admin/categories/CategoriesPage.jsx';
import { BrandsPage } from './pages/admin/brands/BrandsPage.jsx';
import { CustomersPage } from './pages/admin/customers/CustomersPage.jsx';
import { CustomerDetailPage } from './pages/admin/customers/CustomerDetailPage.jsx';
import { TasksPage } from './pages/admin/tasks/TasksPage.jsx';
import { LogisticsPage } from './pages/admin/logistics/LogisticsPage.jsx';
import { PersonalityPage } from './pages/admin/ai/PersonalityPage.jsx';
import { ModelsPage } from './pages/admin/ai/ModelsPage.jsx';
import { AgentsPage } from './pages/admin/ai/AgentsPage.jsx';
import { PlaygroundPage } from './pages/admin/ai/PlaygroundPage.jsx';
import { UsagePage as AiUsagePage } from './pages/admin/ai/UsagePage.jsx';
import { FeedbackPage as AiFeedbackPage } from './pages/admin/ai/FeedbackPage.jsx';
import { JailbreakPage } from './pages/admin/ai/JailbreakPage.jsx';
import { RagPage } from './pages/admin/rag/RagPage.jsx';
import { RegisterPage as PrivacyRegisterPage } from './pages/admin/privacy/RegisterPage.jsx';
import { ConsentsPage as PrivacyConsentsPage } from './pages/admin/privacy/ConsentsPage.jsx';
import { ErasuresPage as PrivacyErasuresPage } from './pages/admin/privacy/ErasuresPage.jsx';
import { AnalyticsPage } from './pages/admin/analytics/AnalyticsPage.jsx';
import { LogsPage } from './pages/admin/logs/LogsPage.jsx';
import { SecurityPage } from './pages/admin/security/SecurityPage.jsx';
import { NotificationsPage } from './pages/admin/notifications/NotificationsPage.jsx';
import { ClientsPage } from './pages/admin/clients/ClientsPage.jsx';
import { SettingsPage } from './pages/admin/settings/SettingsPage.jsx';
import { SystemPage } from './pages/admin/system/SystemPage.jsx';
import { EmployeeHomePage } from './pages/employee/HomePage.jsx';
import { InventoryPage as EmployeeInventoryPage } from './pages/employee/InventoryPage.jsx';
import { NutritionPage as EmployeeNutritionPage } from './pages/employee/NutritionPage.jsx';
import { ChatPage as EmployeeChatPage } from './pages/employee/ChatPage.jsx';
import { KnowledgePage as EmployeeKnowledgePage } from './pages/employee/KnowledgePage.jsx';
import { ProfilePage as EmployeeProfilePage } from './pages/employee/ProfilePage.jsx';
import { CustomersPage as EmployeeCustomersPage } from './pages/employee/CustomersPage.jsx';
import { PetHome } from './pages/account/portal/PetHome.jsx';
import { Timeline } from './pages/account/portal/Timeline.jsx';
import { Gallery } from './pages/account/portal/Gallery.jsx';
import { WellnessPath } from './pages/account/portal/WellnessPath.jsx';
import { ClaimPage } from './pages/account/portal/ClaimPage.jsx';
import { KioskLoginPage } from './pages/account/portal/KioskLoginPage.jsx';
import { PortalProfile } from './pages/account/portal/PortalProfile.jsx';
import { PlatePage } from './pages/account/portal/PlatePage.jsx';
import { AssistantPage } from './pages/account/portal/AssistantPage.jsx';
import { KioskHomePage } from './pages/kiosk/HomePage.jsx';
import { AccountPage } from './pages/AccountPage.jsx';

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/login/mfa" element={<MfaChallengePage />} />

      <Route element={<RequireAuth allow={['owner', 'admin']} />}>
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<OperationsCenterPage />} />
          <Route path="dashboard" element={<AdminDashboardPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="roles" element={<RolesPage />} />
          <Route path="stores" element={<StoresPage />} />
          <Route path="products" element={<ProductsPage />} />
          <Route path="products/new" element={<ProductEditPage />} />
          <Route path="products/:id/edit" element={<ProductEditPage />} />
          <Route path="categories" element={<CategoriesPage />} />
          <Route path="brands" element={<BrandsPage />} />
          <Route path="customers" element={<CustomersPage />} />
          <Route path="customers/:id" element={<CustomerDetailPage />} />
          <Route path="tasks" element={<TasksPage />} />
          <Route path="logistics" element={<LogisticsPage />} />
          <Route path="ai">
            <Route index element={<Navigate to="personality" replace />} />
            <Route path="personality" element={<PersonalityPage />} />
            <Route path="models" element={<ModelsPage />} />
            <Route path="agents" element={<AgentsPage />} />
            <Route path="playground" element={<PlaygroundPage />} />
            <Route path="usage" element={<AiUsagePage />} />
            <Route path="feedback" element={<AiFeedbackPage />} />
            <Route path="jailbreak" element={<JailbreakPage />} />
          </Route>
          <Route path="rag" element={<RagPage />} />
          <Route path="privacy">
            <Route index element={<Navigate to="register" replace />} />
            <Route path="register" element={<PrivacyRegisterPage />} />
            <Route path="consents" element={<PrivacyConsentsPage />} />
            <Route path="erasures" element={<PrivacyErasuresPage />} />
          </Route>
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="clients" element={<ClientsPage />} />
          <Route path="logs" element={<LogsPage />} />
          <Route path="security" element={<SecurityPage />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="system" element={<SystemPage />} />
          <Route path="account" element={<AccountPage />} />
        </Route>
      </Route>

      <Route element={<RequireAuth allow={['seller']} />}>
        <Route path="/employee" element={<EmployeeLayout />}>
          <Route index element={<EmployeeHomePage />} />
          <Route path="customers" element={<EmployeeCustomersPage />} />
          <Route path="customers/:id" element={<CustomerDetailPage basePath="/employee/customers" />} />
          <Route path="tasks" element={<TasksPage />} />
          <Route path="logistics" element={<LogisticsPage />} />
          <Route path="inventory" element={<EmployeeInventoryPage />} />
          <Route path="nutrition" element={<EmployeeNutritionPage />} />
          <Route path="knowledge" element={<EmployeeKnowledgePage />} />
          <Route path="chat" element={<EmployeeChatPage />} />
          <Route path="profile" element={<EmployeeProfilePage />} />
          <Route path="account" element={<AccountPage />} />
        </Route>
      </Route>

      {/* Public: an invited owner activates their account here, no session yet. */}
      <Route path="/account/claim" element={<ClaimPage />} />

      {/* Public: cross-device kiosk login. The phone scans the QR shown on the
          in-store kiosk and lands authenticated here, no session yet. */}
      <Route path="/k/:token" element={<KioskLoginPage />} />

      <Route element={<RequireAuth allow={['user']} />}>
        <Route path="/account" element={<PetLayout />}>
          <Route index element={<PetHome />} />
          <Route path="diario" element={<Timeline />} />
          <Route path="galleria" element={<Gallery />} />
          <Route path="benessere" element={<WellnessPath />} />
          <Route path="piatto" element={<PlatePage />} />
          <Route path="assistente" element={<AssistantPage />} />
          <Route path="profilo" element={<PortalProfile />} />
        </Route>
      </Route>

      <Route path="/kiosk" element={<KioskLayout />}>
        <Route index element={<KioskHomePage />} />
      </Route>

      <Route path="/" element={<RoleRedirect />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
